<template>
  <zt-modal
    class="rounded font-normal"
    max-width="600px"
    :title="$t('message.certificates.modal.title')"
    :active="active"
    :z-index="1001"
    @close="closeDialog"
  >
    <template>
      <div v-if="currentStep === 1" class="mb-4 px-10 py-12">
        <div class="flex items-center">
          <div class="flex-none flex items-center w-24 h-24">
            <zt-icon
              name="search-outline"
              :style="{ fontSize: '4rem'}"
            />
          </div>
          <div>
            <p class="lg:text-zt-xl text-zt-dark-500">{{ $t('message.certificates.modal.search') }}</p>
          </div>
        </div>
        <div class="flex items-center">
          <div class="w-24 hidden lg:block"></div>
          <div class="flex items-center">
            <zt-autocomplete
              :label="$t('message.certificates.modal.select')"
              :noResultsText="$t('message.certificates.modal.noResultsText')"
              :items="items"
              outlined
              returnObject
              clearable
              item-prepend-icon="file-text-outline"
              @focus="openSelect"
              v-model="certificate"
              :disabled="!items.length"
              data-vs-autocomplete="certificates-search-autocomplete-certificate"
            ></zt-autocomplete>
            <div class="loader flex-none" v-if="loading"></div>
          </div>
        </div>
        <p v-if="notAvailableText" class="lg:pl-24 text-base mt-2 text-zt-danger">
          {{ $t('message.certificates.modal.notAvailableText') }}
        </p>
      </div>
      <div v-if="currentStep === 2" class="mb-4 px-10 py-12">
        <div class="flex items-center">
          <div class="flex-none flex items-center w-24 h-24">
            <zt-icon
              name="exclamation-circle-outline"
              class="text-zt-warning"
              :style="{ fontSize: '4rem'}"
            />
          </div>
          <div>
            <p class="lg:text-zt-xl text-zt-dark-500">
              {{ $t('message.certificates.modal.downloadItem', { item: certificate.text }) }}
            </p>
          </div>
        </div>
      </div>
      <div v-if="shownError" class="flex flex-col items-center lg:flex-row px-10 pb-12">
        <zt-alert :color="status" close-icon filled class="modal-message" @afterClosed="hideMessage">
          <div slot="custom-content">
            <div class="flex justify-start items-center p-2">
              <zt-icon v-if="iconName" :name="iconName" class="icon mr-2"></zt-icon>
              <p>{{ message }}</p>
            </div>
          </div>
        </zt-alert>
      </div>
    </template>
    <template #modal-footer>
      <div
        class="zt-modal-footer rounded-b flex justify-end items-center p-4"
      >
        <zt-button
          text
          color="dark-300"
          class="button-cancel mr-4"
          @click="closeDialog"
          data-vs-button="certificates-search-btn-closedialog"
        >
          {{ $t('message.certificates.modal.cancel') }}
        </zt-button>
        <zt-button class="w-25" :disabled="isActive" :loader="downloading" @click="download" data-vs-button="certificates-search-btn-activedownload">
          {{ $t('message.certificates.modal.download') }}
        </zt-button>
      </div>
    </template>
  </zt-modal>
</template>

<script>
import { isEmpty } from 'lodash'
import Company from '@/apiClient/Company'
import Users from '@/apiClient/Users'
const icons = {
  'danger': 'exclamation-circle-solid',
  'warning': 'warning-circle-solid',
  'info': 'info-circle-solid'
}
export default {
  name: 'SearchCertificatesDialog',
  props: {
    active: {
      type: Boolean,
      default: false
    }
  },
  inject: [ 'openDialog' ],
  data() {
    return {
      downloading: false,
      downloadActive: false,
      certificate: '',
      currentStep: 1,
      items: [],
      loading: false,
      notAvailableText: false,
      shownError: false,
      message: '',
      status: 'danger'
    }
  },
  watch: {
    active(bool) {
      if (bool === true) {
        this.certificate = ''
        this.currentStep = 1
        this.getCertificates()
      }
    }
  },
  computed: {
    isActive() {
      return isEmpty(this.certificate)
    },
    iconName() {
      return icons[this.status]
    }
  },
  created() {
    this.companyApi = new Company()
    this.usersApi = new Users()
  },
  methods: {
    closeDialog() {
      if (this.downloading || this.loading) return
      this.openDialog(false)
      this.hideMessage()
    },
    async download() {
      if (this.currentStep === 2) {
        try {
          this.downloading = true
          const { data: { data }} = await this.usersApi.getCertificateURL(this.certificate.value)
          const element = document.createElement('a')
          element.href = `${data.link}`
          element.click()
          this.downloading = false
          this.$emit('reload-history')
          this.closeDialog()
        } catch (e) {
          this.downloading = false
          if (e.response && e.response.status === 404) {
            this.message = this.$t('message.certificates.error.download')
            this.status = 'danger'
            this.shownError = true
          }
        }
      }
      if (this.currentStep === 1) {
        this.currentStep = 2
      }
    },
    openSelect() {
      this.$nextTick(() => {
        const el = document.querySelector('.zt-autocomplete-content')
        if (el) {
          el.style.zIndex = 1004
          Array.from(el.querySelectorAll('.zt-icon'))
            .forEach(icon => icon.style.fontSize = '1.5rem')
        }
      })
    },
    async getCertificates() {
      this.loading = true
      this.notAvailableText = false
      try {
        const { data: { data }} = await this.companyApi.getCertificatesAvailables()
        this.items = data.map(item => {
          return {
            text: item.nombre,
            value: item.id
          }
        })
        this.loading = false
      } catch (e) {
        this.items = []
        this.loading = false
        if (e.response.status === 404) {
          this.notAvailableText = true
        }
      }
    },
    hideMessage() {
      this.shownError = false
    }
  }
}
</script>

<style scoped lang="scss">
.zt-modal-footer {
  background-color: #EEEEEE;
}
::v-deep {
  .zt-modal {
    box-shadow: 0 3px 4px rgba(0, 0, 0, 0.14), 0 3px 3px rgba(0, 0, 0, 0.12),
    0 1px 8px rgba(0, 0, 0, 0.2);
  }
  .zt-modal.rounded {
    border-radius: .375rem!important;
  }
  button.button-cancel {
    color: #707070!important;
    background-color: transparent!important;
  }
  .zt-autocomplete-outlined {
    height: 50px!important;
  }
  .zt-autocomplete-outlined .zt-autocomplete-label {
    padding-left: 15px!important;
  }
  .zt-autocomplete-outlined.zt-autocomplete-focused .zt-autocomplete-label,
  .zt-autocomplete-outlined .zt-autocomplete-label.zt-autocomplete-label-active {
    padding-left: 5px!important;
    transform: translateY(-47px) translateX(5px) scale(.75)!important;
    font-size: 1rem!important;
  }
}
.loader {
  border: 2px solid #898DFF;
  border-top: 2px solid transparent;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  animation: spin-data-v-fd57c644 2s linear infinite;
  margin: 5px;
}
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
::v-deep {
  .zt-icon-times {
    margin-top: 7px;
  }
  .icon {
    font-size: 2rem;
  }
  .modal-message {
    padding-left: 8px !important;
    padding-right: 8px !important;
  }
}
.mrg-0 {
  margin: 0!important;
}
</style>
